// ./src/components/features/Events/CoverFramePicker.tsx
//
// "Choose a frame" — ANY moment of the event's cover video as its cover PHOTO (owner, 2026-10-27: "user should be able to
// select any frame from a video they selected for their event as event image to be displayed as video's thumbnail before
// playing"). The cover photo IS the video's poster on the website (`MediaHero`: the image, else a provider thumbnail), so
// picking a frame here is what guests see before the video plays.
//   • the host scrubs the event's own video (the slider is exact; the player's controls work too) and takes the frame on
//     screen: drawn to a canvas at the video's own size, uploaded as an ordinary cover image (`purpose: cover`, in the
//     event's folder), and set as the cover photo — replaceable and removable like any other;
//   • only for a video FILE (an upload, or a direct link): YouTube and Vimeo play in a frame we cannot read
//     (`canPickFrameFrom`); a file whose host refuses CORS cannot be drawn either, and says so beside the button.
// ⚠ THE VIDEO IS LOADED INTO THE PAGE (a local copy), not played from its URL: our storage answers an anonymous request in
//   its old default API version, which leaves out `Accept-Ranges`, so the browser cannot SEEK it (`seekable` is [0, 0] and
//   setting `currentTime` snaps back to 0 — found 2026-10-27). A local copy is fully seekable and never taints the canvas.
//   A file that cannot be fetched (no CORS) falls back to its URL. ⓘ The real fix is the storage account's default service
//   version (an owner step — ROADMAP), which also lets guests scrub a cover video on the site.
// ⓘ Reuses what the Storage page's "Capture frame" does (`FilePreviewModal`), with a scrubber and an event's upload.
// ⓘ Local state, like the MediaSelector it sits beside: it acts inside the event's details form, not on the page.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Group, Loader, Slider, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppDrawer } from "@/components/generic";
import { useApp } from "@/contexts/AppContext";
import { useUploadedFiles } from "@/hooks/useUploadedFiles";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { buildFullName, makeUniqueFilename } from "@/utils/nameHelpers";

export { canPickFrameFrom } from "@/utils/coverFrame";

/** "1:07.4" — the position on the scrubber. */
const positionLabel = (seconds: number): string => {
  const s = Math.max(0, seconds);

  return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;
};

export interface CoverFramePickerProps {
  opened: boolean;
  onClose: () => void;
  /** The event's cover video (a file). */
  videoUrl: string | null | undefined;
  /** Where the frame is filed in storage (the event's folder). */
  contextPath?: string | null;
  /** The uploaded frame, to set as the cover photo. */
  onPicked: (picked: { uploadedFileId: string | null; url: string }) => void;
}

export const CoverFramePicker: React.FC<CoverFramePickerProps> = ({ opened, onClose, videoUrl, contextPath, onPicked }) => {
  const { t } = useTranslation(["events", "common"]);
  const { user } = useApp();
  const { create } = useUploadedFiles({ autoLoad: false, pageSize: DEFAULT_PAGE_SIZE });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The video as the player gets it: a local copy (seekable), else its URL; null while it loads.
  const [playable, setPlayable] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setDuration(0);
      setPosition(0);
      setSaving(false);
      setError(null);
    }
  }, [opened, videoUrl]);

  useEffect(() => {
    setPlayable(null);

    if (!opened || !videoUrl) {
      return;
    }

    let current = true;
    let local: string | null = null;

    void fetch(videoUrl, { mode: "cors" })
      .then((res) => (res.ok ? res.blob() : Promise.reject(new Error(String(res.status)))))
      .then((blob) => {
        local = URL.createObjectURL(blob);

        if (current) {
          setPlayable(local);
        } else {
          URL.revokeObjectURL(local);
        }
      })
      .catch(() => {
        if (current) {
          setPlayable(videoUrl);
        }
      });

    return () => {
      current = false;

      if (local) {
        URL.revokeObjectURL(local);
      }
    };
  }, [opened, videoUrl]);

  const seek = useCallback((seconds: number) => {
    const video = videoRef.current;

    setPosition(seconds);

    if (video) {
      video.pause();
      video.currentTime = seconds;
    }
  }, []);

  const takeFrame = useCallback(async () => {
    const video = videoRef.current;

    if (!video || !video.videoWidth) {
      setError(t("events:details.frameNotReady", { defaultValue: "The video has not loaded yet. Wait a moment and try again." }));

      return;
    }

    setError(null);
    setSaving(true);

    // A seek still in flight would draw the frame it is leaving.
    if (video.seeking) {
      await new Promise<void>((resolve) => video.addEventListener("seeked", () => resolve(), { once: true }));
    }

    const canvas = document.createElement("canvas");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    let blob: Blob | null = null;

    try {
      canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
      blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.9));
    } catch {
      blob = null;
    }

    if (!blob) {
      setSaving(false);
      setError(
        t("events:details.frameBlocked", {
          defaultValue: "A frame can't be taken from this video — the site it is on does not allow it. Upload the video here instead.",
        }),
      );

      return;
    }

    const filename = `cover-frame-${Math.round(video.currentTime * 10) / 10}s.jpg`;
    const file = await create({
      files: [new File([blob], filename, { type: "image/jpeg" })],
      name: makeUniqueFilename(filename),
      originalFilename: filename,
      mimeType: "image/jpeg",
      contextPath: contextPath ?? undefined,
      purpose: "cover",
      uploadedByKind: "user",
      uploadedById: user?.id ?? undefined,
      uploadedByName: user ? buildFullName(user) : undefined,
    });

    setSaving(false);

    if (!file?.id || !file.fileUrl) {
      setError(t("common:mediaSelector.uploadFailed", { defaultValue: "Upload failed. Please try again." }));

      return;
    }

    onPicked({ uploadedFileId: String(file.id), url: String(file.fileUrl) });
    onClose();
  }, [contextPath, create, onClose, onPicked, t, user]);

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={t("events:details.frameTitle", { defaultValue: "Choose a frame for the cover photo" })}
      size="xl"
      actionType="confirm-cancel"
      // The moment the host scrubbed to is the work: closing after moving asks first.
      isDirty={position > 0 && !saving}
      confirmLabel={t("events:details.frameUse", { defaultValue: "Use this frame" })}
      confirmIcon="Photo"
      onConfirm={() => void takeFrame()}
      confirmDisabled={!duration}
      loading={saving}
      errors={error}
    >
      <Stack gap="md">
        <Text size="md" c="dimmed">
          {t("events:details.frameHint", {
            defaultValue: "Guests see this picture on your event page before the video plays. Move to the moment you want, then use it.",
          })}
        </Text>
        {!playable && videoUrl ? (
          <Group gap="sm" justify="center" py="xl">
            <Loader size="sm" />
            <Text size="md" c="dimmed">
              {t("events:details.frameLoading", { defaultValue: "Loading the video…" })}
            </Text>
          </Group>
        ) : null}
        {playable ? (
          <video
            ref={videoRef}
            src={playable}
            crossOrigin="anonymous"
            controls
            muted
            playsInline
            preload="auto"
            style={{ width: "100%", maxHeight: "55vh", background: "#000", borderRadius: 8 }}
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;

              setDuration(Number.isFinite(d) ? d : 0);
            }}
            onTimeUpdate={(e) => {
              const now = e.currentTarget.currentTime;

              setPosition(now);
            }}
          />
        ) : null}
        <Stack gap={4}>
          <Slider
            min={0}
            max={duration || 1}
            step={0.1}
            value={Math.min(position, duration || 1)}
            onChange={seek}
            disabled={!duration}
            label={positionLabel}
            aria-label={t("events:details.frameScrub", { defaultValue: "Position in the video" })}
          />
          <Group justify="space-between">
            <Text size="md" c="dimmed">
              {positionLabel(position)}
            </Text>
            <Text size="md" c="dimmed">
              {duration ? positionLabel(duration) : "—"}
            </Text>
          </Group>
        </Stack>
      </Stack>
    </AppDrawer>
  );
};
